/**
 * Regra de mescla do resync periódico da conversa.
 *
 * O Realtime perde eventos em rede móvel, aba suspensa e reconexão, então a cada poucos
 * segundos relemos a janela recente do banco. O que essa releitura NÃO pode fazer é crescer
 * a lista para trás: buscar as N mais recentes numa conversa com menos de N carregadas
 * arrastava histórico antigo para dentro da tela sozinho, empurrando o conteúdo para baixo
 * enquanto o usuário lia. O que é mais antigo que o topo carregado chega pela paginação,
 * quando ele pedir.
 *
 * Extraída do hook para ser testável: é a regra que decide o que aparece na tela.
 */

interface SyncableMessage {
  id: string | number
  wa_message_id?: string | null
  sent_at: string
}

export interface MergeRecentResult<T> {
  messages: T[]
  /** Falso quando o sync só atualizou mensagens já visíveis — evita reordenar à toa. */
  appended: boolean
  /** Falso quando nada mudou; aí `messages` é o array original, e o React nem re-renderiza. */
  changed: boolean
}

/**
 * Compara só os campos escalares. Os jsonb (`raw_payload`, `marketing_*`) chegam como objeto
 * novo a cada fetch, então compará-los por referência marcaria toda mensagem como alterada e
 * recriaria a lista inteira a cada resync — justamente o que estamos evitando. É seguro:
 * depois do insert, só campos escalares mudam (status, deleted_at, ai_processed_at,
 * media_url, processed_media_url); os jsonb são gravados uma vez e nunca reescritos.
 */
function hasScalarChange<T extends object>(existing: T, incoming: T): boolean {
  for (const key of Object.keys(incoming) as (keyof T)[]) {
    const before = existing[key]
    const after = incoming[key]
    if (before === after) continue
    // Um dos dois é objeto/array: fora da comparação, pelo motivo acima.
    if (typeof after === 'object' && after !== null) continue
    if (typeof before === 'object' && before !== null) continue
    return true
  }
  return false
}

/**
 * @param loaded    O que já está na tela, em ordem cronológica.
 * @param recent    A janela recente vinda do banco, em ordem cronológica.
 * @param belongs   Guarda de contato: o canal é por conta, não por conversa.
 */
export function mergeRecentMessages<T extends SyncableMessage>(
  loaded: readonly T[],
  recent: readonly T[],
  belongs: (message: T) => boolean = () => true
): MergeRecentResult<T> {
  const messages = [...loaded]

  // Fronteira: nada mais antigo que o topo já carregado entra por aqui.
  let oldestLoadedAt: number | null = null
  for (const message of messages) {
    const time = new Date(message.sent_at).getTime()
    if (Number.isNaN(time)) continue
    if (oldestLoadedAt === null || time < oldestLoadedAt) oldestLoadedAt = time
  }

  // Índice por id e por wa_message_id — procurar por mensagem era O(n×m).
  const positions = new Map<string, number>()
  messages.forEach((message, position) => {
    positions.set(`id:${message.id}`, position)
    if (message.wa_message_id) positions.set(`wa:${message.wa_message_id}`, position)
  })

  let appended = false
  let changed = false

  for (const incoming of recent) {
    if (!belongs(incoming)) continue

    const at =
      positions.get(`id:${incoming.id}`) ??
      (incoming.wa_message_id ? positions.get(`wa:${incoming.wa_message_id}`) : undefined)

    // Já visível: atualiza no lugar (status, transcrição, exclusão) sem mover nada.
    if (at !== undefined) {
      // Sem mudança real, mantém o MESMO objeto. É o que deixa o React.memo das bolhas
      // barrar o render: recriar 30 objetos idênticos a cada 10s repintava a conversa toda.
      if (!hasScalarChange(messages[at], incoming)) continue
      messages[at] = { ...messages[at], ...incoming }
      changed = true
      continue
    }

    const time = new Date(incoming.sent_at).getTime()
    if (oldestLoadedAt !== null && !Number.isNaN(time) && time < oldestLoadedAt) continue

    messages.push(incoming)
    positions.set(`id:${incoming.id}`, messages.length - 1)
    if (incoming.wa_message_id) positions.set(`wa:${incoming.wa_message_id}`, messages.length - 1)
    appended = true
    changed = true
  }

  // Nada mudou: devolve o array original para o setState do React não disparar render.
  if (!changed) return { messages: loaded as T[], appended: false, changed: false }

  if (appended) {
    messages.sort((a, b) => {
      const diff = new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime()
      return diff || String(a.id).localeCompare(String(b.id))
    })
  }

  return { messages, appended, changed }
}
